const SAMPLE_RATE = 48000;

const WORKLET_SOURCE = `
class AppAudioPlayer extends AudioWorkletProcessor {
  constructor() {
    super();
    this.capacity = ${SAMPLE_RATE};
    this.left = new Float32Array(this.capacity);
    this.right = new Float32Array(this.capacity);
    this.readIndex = 0;
    this.writeIndex = 0;
    this.size = 0;
    this.primed = false;
    this.primeFrames = ${SAMPLE_RATE / 50};
    this.maxFrames = ${(SAMPLE_RATE * 3) / 20};
    this.port.onmessage = (event) => this.push(new Int16Array(event.data));
  }

  push(samples) {
    const frames = samples.length >> 1;
    for (let i = 0; i < frames; i++) {
      this.left[this.writeIndex] = samples[2 * i] / 32768;
      this.right[this.writeIndex] = samples[2 * i + 1] / 32768;
      this.writeIndex = (this.writeIndex + 1) % this.capacity;
      if (this.size < this.capacity) {
        this.size++;
      } else {
        this.readIndex = (this.readIndex + 1) % this.capacity;
      }
    }
    if (this.size > this.maxFrames) {
      const drop = this.size - this.primeFrames;
      this.readIndex = (this.readIndex + drop) % this.capacity;
      this.size -= drop;
    }
    if (this.size >= this.primeFrames) this.primed = true;
  }

  process(_inputs, outputs) {
    const left = outputs[0][0];
    const right = outputs[0][1] || left;
    for (let i = 0; i < left.length; i++) {
      if (this.primed && this.size > 0) {
        left[i] = this.left[this.readIndex];
        right[i] = this.right[this.readIndex];
        this.readIndex = (this.readIndex + 1) % this.capacity;
        this.size--;
      } else {
        left[i] = 0;
        right[i] = 0;
        this.primed = false;
      }
    }
    return true;
  }
}

registerProcessor("app-audio-player", AppAudioPlayer);
`;

export const startAppAudioTrack = async (sourceId) => {
  const api = window.electron;
  if (!api?.startAppAudioCapture || !api?.onAppAudioChunk) return null;

  let context = null;
  let removeListener = null;
  let captureStarted = false;

  const cleanup = () => {
    removeListener?.();
    removeListener = null;
    if (captureStarted) api.stopAppAudioCapture?.();
    captureStarted = false;
    context?.close().catch(() => {});
    context = null;
  };

  try {
    context = new AudioContext({ sampleRate: SAMPLE_RATE });
    const moduleUrl = URL.createObjectURL(
      new Blob([WORKLET_SOURCE], { type: "application/javascript" })
    );
    try {
      await context.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }

    const player = new AudioWorkletNode(context, "app-audio-player", {
      numberOfInputs: 0,
      outputChannelCount: [2],
    });
    const destination = context.createMediaStreamDestination();
    player.connect(destination);

    removeListener = api.onAppAudioChunk((chunk) => {
      const copy = chunk.slice();
      player.port.postMessage(copy.buffer, [copy.buffer]);
    });

    captureStarted = await api.startAppAudioCapture(sourceId);
    if (!captureStarted) {
      cleanup();
      return null;
    }

    await context.resume();

    const track = destination.stream.getAudioTracks()[0];
    let stopped = false;

    return {
      track,
      stop: () => {
        if (stopped) return;
        stopped = true;
        track.stop();
        cleanup();
      },
    };
  } catch (error) {
    console.error("Failed to start app audio capture:", error);
    cleanup();
    return null;
  }
};
